import React, { FC, useMemo, useRef, useEffect } from "react";
import { FixedSizeList, VariableSizeList } from "react-window";
import cn from "clsx";
import { useTranslation } from "react-i18next";
import ReactHtmlParser from "react-html-parser";
import CustomScrollbars from "@/components/customScrollbars";
import { DropdownListProps, DropdownOptionProps } from "./type";

const DropdownList = ({
  data,
  selectedValue,
  handleChange,
  className,
  scrollbarMaxHeight,
  itemHeight = 23.5,
  withSearchInput,
  noDataContent
}: DropdownListProps) => {
  const { t } = useTranslation("common");
  const listRef = useRef<any>(null);

  const maxHeight = (scrollbarMaxHeight || 250) - (withSearchInput ? 50 : 0);

  // Reset cache if it's a variable list and data changes
  useEffect(() => {
    if (listRef.current?.resetAfterIndex) {
      listRef.current.resetAfterIndex(0);
    }
  }, [data]);

  const scrollContainerHeight = useMemo(() => {
    if (itemHeight === "auto") return maxHeight;
    if (typeof itemHeight === "function") {
      let total = 0;
      for (let i = 0; i < data.length; i++) {
        total += itemHeight(i);
      }
      return Math.min(total, maxHeight);
    }
    return Math.min(data.length * (itemHeight as number), maxHeight);
  }, [data, itemHeight, maxHeight]);

  if (!data.length) {
    return <p className="no_data_found">{noDataContent || t("noDataFound")}</p>;
  }
  // Handle "auto" height: render a standard list without virtualization
  if (itemHeight === "auto") {
    return (
      <div className={className} style={{ height: scrollContainerHeight }}>
        <CustomScrollbars onScroll={() => {}} style={{}}>
          <ul className="dropdown_content_container">
            {data.map((item, index) => (
              <DropdownItem
                key={index}
                item={item}
                isSelected={selectedValue === item}
                onClick={handleChange}
              />
            ))}
          </ul>
        </CustomScrollbars>
      </div>
    );
  }

  return (
    <div className={className} style={{ height: scrollContainerHeight }}>
      {typeof itemHeight === "function" ? (
        <VariableSizeList
          ref={listRef}
          height={scrollContainerHeight}
          width="100%"
          itemCount={data.length}
          itemData={{
            items: data,
            selectedValue,
            handleChange
          }}
          itemSize={itemHeight}
          outerElementType={CustomScrollbars}
          innerElementType={InnerList}
        >
          {DropdownItemRow}
        </VariableSizeList>
      ) : (
        <FixedSizeList
          height={scrollContainerHeight}
          width="100%"
          itemCount={data.length}
          itemData={{
            items: data,
            selectedValue,
            handleChange
          }}
          itemSize={itemHeight as number}
          outerElementType={CustomScrollbars}
          innerElementType={InnerList}
        >
          {DropdownItemRow}
        </FixedSizeList>
      )}
    </div>
  );
};

const DropdownItem: FC<{
  item: DropdownOptionProps;
  isSelected: boolean;
  onClick: (item: any) => void;
  style?: React.CSSProperties;
}> = ({ item, isSelected, onClick, style }) => (
  <li
    style={{
      ...style,
      display: "block",
      boxSizing: "border-box"
    }}
    className={cn({
      selected_value: isSelected
    })}
    onClick={() => onClick(item)}
  >
    {item.labelElement ? item.labelElement : ReactHtmlParser(item.label)}
  </li>
);

const DropdownItemRow: FC<{
  data: {
    items: DropdownListProps["data"];
    selectedValue?: DropdownListProps["selectedValue"];
    handleChange: DropdownListProps["handleChange"];
  };
  index: number;
  style: React.CSSProperties;
}> = ({ data, index, style }) => (
  <DropdownItem
    item={data.items[index]}
    isSelected={data.selectedValue === data.items[index]}
    onClick={data.handleChange}
    style={style}
  />
);

const InnerList = React.forwardRef<
  HTMLUListElement,
  React.HTMLProps<HTMLUListElement>
>(({ children, ...props }, ref) => (
  <ul ref={ref} className="dropdown_content_container" {...props}>
    {children}
  </ul>
));

export default DropdownList;
